import DynamicImage from '@/components/commons/dynamic-image';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, formatIDR, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import {
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { CircleCheck, MinusIcon, PlusIcon } from 'lucide-react';
import { Dispatch, SetStateAction } from 'react';

type CardGiftProductItemProps = {
  product: BasePromotionRewardProductItem;
  setSelectedTab: Dispatch<SetStateAction<'product_group' | 'product_item'>>;
  maxQty: number;
  totalQty: number;
  selectedProducts: BaseFreeGift[];
  selectedProduct: BasePromotionRewardProduct;
  handleUpdateQty: (
    productGroup: BasePromotionRewardProduct,
    item: BasePromotionRewardProductItem,
    newQty: number
  ) => void;
  colorScheme?: ThemeColorSchemeType;
};

export default function CardGiftProductItem({
  product,
  setSelectedTab,
  maxQty,
  totalQty,
  selectedProducts,
  selectedProduct,
  handleUpdateQty,
  colorScheme,
}: CardGiftProductItemProps) {
  const getItemQty = (productGroupId: string, itemId: string) => {
    const group = selectedProducts.find((g) => g.product_group.product_group_id === productGroupId);
    const item = group?.items.find((i) => i.item_id === itemId);
    return Number(item?.qty) || 0;
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3.5 rounded p-1',
        product.stock === 0 ? 'cursor-not-allowed' : 'cursor-pointer'
      )}
      onClick={() => {
        if (product.stock === 0) return;
        setSelectedTab('product_item');
      }}
    >
      <div className='flex items-start gap-3.5'>
        <DynamicImage
          src={product.product_assets?.[0] ?? String(selectedProduct.product_assets?.[0])}
          alt='Product Image'
          width={80}
          height={80}
          className={cn(
            'aspect-square size-[70px] rounded-sm object-cover',
            product.stock === 0 && 'grayscale'
          )}
          sizes='100vw'
          draggable={false}
        />

        <div className='flex flex-col gap-1 text-xs'>
          {product.stock === 0 && (
            <div className='w-auto'>
              <Badge variant='secondary' className='p-1 py-0.5 text-[10px] font-normal'>
                Sold Out
              </Badge>
            </div>
          )}
          <span
            className={cn(
              'line-clamp-2 font-bold',
              product.stock === 0
                ? 'text-gray-400'
                : isEmpty(colorScheme)
                ? 'text-[#58C151]'
                : 'text-[var(--text-color)]'
            )}
            style={
              {
                '--text-color': colorScheme?.text || '#58C151',
              } as React.CSSProperties
            }
          >
            {product.item_name}
          </span>
          <div className='flex items-center gap-1 font-medium'>
            <p
              style={
                !isEmpty(colorScheme) ? { color: colorScheme?.textSecondary } : { color: '#AD883F' }
              }
            >
              Free
            </p>{' '}
            <p className='text-gray-300 line-through'>{formatIDR(Number(product.sell_price))}</p>
          </div>
          <div className='flex w-[80px] items-center rounded border p-0.5'>
            <button
              className='h-full px-3'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleUpdateQty(selectedProduct, product, -1);
              }}
              disabled={
                (product.stock ?? 0) <= 0 ||
                getItemQty(selectedProduct.product_group_id, product.item_id) <= 0
              }
            >
              <MinusIcon
                className={cn(
                  'h-3 w-3',
                  (getItemQty(selectedProduct.product_group_id, product.item_id) <= 0 ||
                    product.stock === 0) &&
                    'text-gray-300'
                )}
              />
            </button>

            <span className='text-xs'>
              {getItemQty(selectedProduct.product_group_id, product?.item_id || '0')}
            </span>

            <button
              className='h-full px-3'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleUpdateQty(selectedProduct, product, 1);
              }}
              disabled={
                (product.stock ?? 0) <= 0 ||
                totalQty >= maxQty ||
                getItemQty(selectedProduct.product_group_id, product.item_id) >=
                  Math.min(product.stock ?? 0, maxQty)
              }
            >
              <PlusIcon
                className={cn(
                  'h-3 w-3',
                  getItemQty(selectedProduct.product_group_id, product.item_id) >= maxQty ||
                    product.stock === 0 ||
                    totalQty >= maxQty
                    ? 'text-gray-300'
                    : ''
                )}
              />
            </button>
          </div>
        </div>
      </div>

      {getItemQty(selectedProduct.product_group_id, product.item_id) > 0 && (
        <div className='flex items-center pr-1'>
          <Checkbox
            checked={getItemQty(selectedProduct.product_group_id, product.item_id) > 0}
            className='mt-1 size-5 rounded-full border-2 data-[state=checked]:border-[var(--checkbox-color)] data-[state=checked]:bg-[var(--checkbox-color)] data-[state=checked]:text-white'
            style={
              {
                '--checkbox-color': colorScheme?.text || '#58C151',
              } as React.CSSProperties
            }
            isCustom
          >
            <CircleCheck className='h-6 w-6 text-red-500' />
          </Checkbox>
        </div>
      )}
    </div>
  );
}
